/**
 * Where product analytics goes, and on which hosts it runs at all.
 *
 * The key is PostHog's public project key: it is meant to sit in the page and
 * can only send events, never read them. It lives here, once, so the hosts the
 * Worker's Content-Security-Policy allows and the hosts the client talks to
 * cannot drift apart without someone reading both.
 */

/** PostHog project 649944 (US Cloud). Public by design; not a secret. */
export const POSTHOG_KEY = 'phc_r7gbUJV2NwDUtK4ZFmq3qXmB4AciuWwgeiVvsuaJSLgy';
/** Where events, flags and recordings are sent. */
export const POSTHOG_HOST = 'https://us.i.posthog.com';
/** Where PostHog serves the code it loads lazily (the session recorder, remote config). */
export const POSTHOG_ASSETS_HOST = 'https://us-assets.i.posthog.com';

export type AnalyticsEnvironment = 'production' | 'staging';

const PRODUCTION_HOSTS = new Set(['krevya.com', 'www.krevya.com', 'app.krevya.com']);
const STAGING_HOSTS = new Set(['staging.krevya.com']);

/**
 * The environment a host reports as, or null where nothing may be sent.
 *
 * Only the real deployments count. A developer's machine, a preview build and
 * the end-to-end suite all run on localhost, so they send nothing and never
 * need the network; anything else unrecognised is treated the same way rather
 * than guessed at.
 */
export function analyticsEnvironment(hostname: string): AnalyticsEnvironment | null {
  const host = hostname.toLowerCase();
  if (PRODUCTION_HOSTS.has(host)) return 'production';
  if (STAGING_HOSTS.has(host)) return 'staging';
  return null;
}

/** Staff screens: never recorded and never captured, whoever is looking at them. */
export function isPrivatePath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

/**
 * Screens session replay never records, even with consent: staff screens,
 * everything to do with signing in (the second factor happens inside
 * `/login`), billing and checkout, and settings. Events are still counted on
 * them, apart from the staff screens, which send nothing at all.
 */
const REPLAY_EXCLUDED = [
  '/admin',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/two-factor',
  '/app/billing',
  '/checkout',
  '/app/settings',
  '/app/account',
];

export function isReplayExcludedPath(pathname: string): boolean {
  return REPLAY_EXCLUDED.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Query parameters that may carry a secret or a person: sign-in and reset
 * tokens, OAuth codes and state (Shopify's `hmac` and `shop` among them),
 * session ids, email addresses. Matched by name, case-insensitively, anywhere
 * in the name, so `reset_token` and `userEmail` go too.
 */
const SENSITIVE_PARAM =
  /token|code|email|e-mail|mail|state|session|password|passwd|secret|key|auth|signature|sig|hmac|shop|nonce|otp|ticket/i;

/** An address with its sensitive query parameters removed; anything unparseable comes back unchanged. */
export function sanitizeUrl(value: string): string {
  if (!value.includes('?')) return value;
  let url: URL;
  try {
    url = new URL(value, 'https://placeholder.invalid');
  } catch {
    return value;
  }
  let changed = false;
  for (const name of [...url.searchParams.keys()]) {
    if (SENSITIVE_PARAM.test(name)) {
      url.searchParams.delete(name);
      changed = true;
    }
  }
  if (!changed) return value;
  if (url.origin === 'https://placeholder.invalid') return `${url.pathname}${url.search}${url.hash}`;
  return url.toString();
}

/** Every string property that holds an address (`$current_url`, `$referrer`, …) is sanitized in place. */
export function sanitizeUrlProperties(properties: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value !== 'string') continue;
    if (/url|referrer|href/i.test(key)) properties[key] = sanitizeUrl(value);
  }
}
